package pl.nickers.match3

/**
 * JS Browser audio implementation using the Web Audio API for SFX and an
 * HTMLAudioElement for background music.
 *
 * Web Audio API gives low-latency, concurrent SFX playback; HTMLAudioElement
 * handles the BGM track (better browser autoplay/suspend semantics for long tracks).
 *
 * **Asset placement** (served by the webpack dev server / production bundle):
 *   SFX  : `composeApp/src/jsMain/resources/audio/sfx_<sound_event_lowercase>.mp3`
 *   Music: `composeApp/src/jsMain/resources/audio/bgm_game.mp3`
 *
 * **Placeholder**: when a fetch fails (file not found), the SFX slot falls back to an
 * [OscillatorNode] beep at [SoundEvent.placeholderFreqHz] so game events are audible
 * during development.  BGM has no placeholder (silence when file is absent).
 *
 * Autoplay: [startMusic] must be called from a user-gesture handler (already wired
 * via `GameViewModel.ensureMusicStarted`) to satisfy browser autoplay policies.
 */
actual class AudioPlayer actual constructor() {

    private var audioContext: dynamic = null

    // Decoded AudioBuffer per event; null = use oscillator placeholder.
    private val sfxBuffers = mutableMapOf<SoundEvent, dynamic>()

    // Placeholder frequencies for events whose file fetch failed.
    private val placeholderFreqs = mutableMapOf<SoundEvent, Int>()

    private var musicEl: dynamic = null

    private var sfxVolume: Float = 1f
    private var sfxMuted: Boolean = false
    private var musicVolume: Float = 0.5f
    private var musicMuted: Boolean = false

    actual fun initialize() {
        audioContext = js("new (window.AudioContext || window.webkitAudioContext)()")

        for (event in SoundEvent.entries) {
            val url = "audio/sfx_${event.name.lowercase()}.mp3"
            fetchAndDecode(url, event)
        }

        musicEl = js("new Audio()")
        musicEl.src = "audio/bgm_game.mp3"
        musicEl.loop = true
        musicEl.volume = if (musicMuted) 0.0 else musicVolume.toDouble()
    }

    actual fun release() {
        sfxBuffers.clear()
        placeholderFreqs.clear()
        runCatching { audioContext?.close() }
        audioContext = null
        if (musicEl != null) musicEl.pause()
        musicEl = null
    }

    actual fun playSfx(event: SoundEvent) {
        if (sfxMuted) return
        val ctx = audioContext ?: return
        val buffer = sfxBuffers[event]
        if (buffer != null) {
            playBuffer(ctx, buffer, sfxVolume.toDouble())
        } else {
            // Placeholder: short oscillator beep.
            val freq = placeholderFreqs[event] ?: event.placeholderFreqHz
            playOscillator(ctx, freq.toDouble(), sfxVolume.toDouble(), durationSec = 0.15)
        }
    }

    actual fun setSfxVolume(volume: Float) {
        sfxVolume = volume.coerceIn(0f, 1f)
    }

    actual fun setSfxMuted(muted: Boolean) {
        sfxMuted = muted
    }

    actual fun startMusic() {
        runCatching { audioContext?.resume() }
        runCatching { musicEl?.play() }
    }

    actual fun stopMusic() {
        runCatching { musicEl?.pause() }
    }

    actual fun setMusicVolume(volume: Float) {
        musicVolume = volume.coerceIn(0f, 1f)
        if (!musicMuted && musicEl != null) musicEl.volume = musicVolume.toDouble()
    }

    actual fun setMusicMuted(muted: Boolean) {
        musicMuted = muted
        if (musicEl != null) musicEl.volume = if (muted) 0.0 else musicVolume.toDouble()
    }

    // ---- Internal callbacks from JS promises --------------------------------

    @JsName("storeSfxBuffer")
    internal fun storeSfxBuffer(event: SoundEvent, buffer: dynamic) {
        sfxBuffers[event] = buffer
    }

    @JsName("storePlaceholderFreq")
    internal fun storePlaceholderFreq(event: SoundEvent, freq: Int) {
        placeholderFreqs[event] = freq
    }

    // ---- Private helpers ----------------------------------------------------

    private fun fetchAndDecode(url: String, event: SoundEvent) {
        val ctx = audioContext ?: return
        @Suppress("UNUSED_VARIABLE")
        val self = this
        @Suppress("UNUSED_VARIABLE")
        val placeholderFreq = event.placeholderFreqHz
        js("""
            fetch(url)
                .then(function(r) { return r.arrayBuffer(); })
                .then(function(ab) { return ctx.decodeAudioData(ab); })
                .then(function(buf) { self.storeSfxBuffer(event, buf); })
                .catch(function() { self.storePlaceholderFreq(event, placeholderFreq); });
        """)
    }

    private fun playBuffer(ctx: dynamic, buffer: dynamic, volume: Double) {
        val source = ctx.createBufferSource()
        source.buffer = buffer
        val gain = ctx.createGain()
        gain.gain.value = volume
        source.connect(gain)
        gain.connect(ctx.destination)
        source.start(0)
    }

    private fun playOscillator(ctx: dynamic, freqHz: Double, volume: Double, durationSec: Double) {
        val now = ctx.currentTime as Double
        val osc = ctx.createOscillator()
        osc.type = "sine"
        osc.frequency.value = freqHz
        val gain = ctx.createGain()
        gain.gain.setValueAtTime(volume, now)
        gain.gain.linearRampToValueAtTime(0.0, now + durationSec)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(now)
        osc.stop(now + durationSec)
    }
}
